import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, errText } from '../lib/api';
import { getSocket, joinProblemRoom, leaveProblemRoom, joinAttemptRoom, leaveAttemptRoom } from '../lib/socket';
import Markdown from '../components/Markdown';
import ImageUpload from '../components/ImageUpload';
import { subjectName, levelName, PROBLEM_STATUS, VERDICTS, SEVERITIES } from '../lib/meta';

interface Step {
  comment: string;
  severity: string;
}
interface Attempt {
  _id: string;
  status: string;
  errorMsg?: string;
  createdAt?: string;
  grading?: { verdict: string; summary: string; steps: Step[] };
}
interface Problem {
  _id: string;
  subject: string;
  level: string;
  stem: string;
  stemImage: string;
  status: string;
  errorMsg: string;
  solution: { content: string };
  userId?: { username?: string };
}

export default function ProblemDetailPage() {
  const { id = '' } = useParams();
  const [problem, setProblem] = useState<Problem | null>(null);
  const [myAttempts, setMyAttempts] = useState<Attempt[]>([]);
  const [streamBuf, setStreamBuf] = useState(''); // 解题流式缓冲
  const [solveErr, setSolveErr] = useState('');
  const [solving, setSolving] = useState(false);
  const [attemptImage, setAttemptImage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeAttempt, setActiveAttempt] = useState<any>(null); // 完整作答（含图）
  const [err, setErr] = useState('');
  const bufRef = useRef('');
  const flushTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/xyz/problems/${id}`);
      setProblem(data.data.problem);
      setMyAttempts(data.data.myAttempts);
      setSolving(data.data.problem.status === 'solving');
      setStreamBuf('');
    } catch (e) {
      setErr(errText(e));
    }
  }, [id]);

  const loadAttempt = useCallback(async (attemptId: string) => {
    try {
      const { data } = await api.get(`/xyz/attempts/${attemptId}`);
      setActiveAttempt(data.data.attempt);
    } catch (e) {
      setErr(errText(e));
    }
  }, []);

  // 首次加载（缺这个 effect 时页面永远停在"加载中"——socket 订阅依赖 problem 已就绪）
  useEffect(() => {
    load();
  }, [load]);

  // socket：解题流式 + 批改完成；断线由下方轮询兜底
  useEffect(() => {
    if (!problem) return;
    joinProblemRoom(id);
    const s = getSocket();
    const onToken = ({ chunk }: { chunk: string }) => {
      bufRef.current += chunk;
      if (!flushTimer.current) {
        flushTimer.current = setInterval(() => setStreamBuf(bufRef.current), 100);
      }
    };
    const onDone = () => { cleanupFlush(); setSolving(false); load(); };
    const onSolveErr = ({ error }: { error: string }) => { cleanupFlush(); setSolving(false); setSolveErr(error || '解答生成失败'); load(); };
    const onGradeDone = ({ attemptId: aid }: { attemptId: string }) => { loadAttempt(aid); load(); };
    s.on('solve_token', onToken);
    s.on('solve_done', onDone);
    s.on('solve_error', onSolveErr);
    s.on('grade_done', onGradeDone);
    return () => {
      cleanupFlush();
      leaveProblemRoom(id);
      s.off('solve_token', onToken);
      s.off('solve_done', onDone);
      s.off('solve_error', onSolveErr);
      s.off('grade_done', onGradeDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem?._id]);

  const cleanupFlush = () => {
    if (flushTimer.current) { clearInterval(flushTimer.current); flushTimer.current = null; }
    setStreamBuf(bufRef.current);
  };

  // 打印/导出 PDF 前展开全部折叠块（原题照片/作答照片），保证内容完整（同主站 ReportPage）
  useEffect(() => {
    const openAll = () => document.querySelectorAll('details').forEach((d) => { (d as HTMLDetailsElement).open = true; });
    window.addEventListener('beforeprint', openAll);
    return () => window.removeEventListener('beforeprint', openAll);
  }, []);

  // 浏览器以 document.title 作为 PDF 默认文件名：打印时临时改为「文灶数理-日期-时间」，打印后还原
  const printProblem = () => {
    const now = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    const name = `文灶数理-${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`;
    const original = document.title;
    document.title = name;
    const restore = () => { document.title = original; };
    window.addEventListener('afterprint', restore, { once: true });
    window.print();
    setTimeout(restore, 60000); // ponytail: afterprint 未触发的兜底
  };

  const startSolve = async () => {
    setSolveErr('');
    try {
      await api.post(`/xyz/problems/${id}/solve`);
      setSolving(true);
      bufRef.current = '';
    } catch (e) {
      setSolveErr(errText(e));
    }
  };

  const submitAttempt = async () => {
    if (!attemptImage) return;
    setSubmitting(true);
    setErr('');
    try {
      const { data } = await api.post(`/xyz/problems/${id}/attempts`, { image: attemptImage });
      const aid: string = data.data.attemptId;
      joinAttemptRoom(aid);
      // socket 断线兜底：轮询直到离开 grading
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/xyz/attempts/${aid}`);
          if (r.data.data.attempt.status !== 'grading') {
            clearInterval(poll);
            leaveAttemptRoom(aid);
            setActiveAttempt(r.data.data.attempt);
            load();
          }
        } catch { /* 轮询失败等下一轮 */ }
      }, 10000);
      setAttemptImage('');
    } catch (e) {
      setErr(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (err && !problem) return <p className="mx-auto max-w-3xl px-4 py-10 text-center text-sm text-red-600">{err}</p>;
  if (!problem) return <p className="py-10 text-center text-sm text-gray-400">加载中…</p>;

  const st = PROBLEM_STATUS[problem.status] || PROBLEM_STATUS.pending;

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <div className="flex items-center gap-4 print:hidden">
        <button
          onClick={printProblem}
          title="打印或保存为 PDF"
          className="text-sm text-gray-600 underline decoration-gray-400 underline-offset-4 hover:text-brand-600"
        >
          🖨 打印 / 导出 PDF
        </button>
      </div>
      {/* 题目 */}
      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-700">{subjectName(problem.subject)}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{levelName(problem.level)}</span>
          <span className={`rounded-full px-2 py-0.5 font-medium ${st.cls}`}>{st.label}</span>
          <span className="ml-auto text-gray-400">{problem.userId?.username}</span>
        </div>
        <Markdown text={problem.stem} className="text-[15px]" />
        {problem.stemImage && (
          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-gray-500">原题照片</summary>
            <img src={problem.stemImage} alt="原题" className="mt-2 max-h-96 rounded-lg border border-gray-200" />
          </details>
        )}
      </div>

      {/* AI 解答 */}
      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="mb-3 font-semibold text-gray-900">🤖 AI 解答</h2>
        {problem.status === 'solved' && problem.solution.content ? (
          <Markdown text={problem.solution.content} className="text-[15px]" />
        ) : solving ? (
          <div>
            <p className="mb-2 animate-pulse text-sm text-brand-600">解答生成中，逐步显示…</p>
            {streamBuf && <Markdown text={streamBuf} className="text-[15px]" />}
          </div>
        ) : (
          <div>
            <p className="mb-3 text-sm text-gray-500">
              {problem.status === 'error' ? `上次失败：${problem.errorMsg || '未知错误'}` : '还没有生成解答。'}
            </p>
            {solveErr && <p className="mb-3 text-sm text-red-600">{solveErr}</p>}
            <button
              onClick={startSolve}
              className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 print:hidden"
            >
              ✨ AI 解题（消耗 1 次额度）
            </button>
          </div>
        )}
      </div>

      {/* 我的作答 */}
      {problem.status === 'solved' && (
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">✍️ 我的作答</h2>
          <div className="print:hidden"><ImageUpload dataUrl={attemptImage} onLoaded={setAttemptImage} label="上传手写作答照片" /></div>
          {attemptImage && (
            <button
              onClick={submitAttempt}
              disabled={submitting}
              className="mt-3 rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 print:hidden"
            >
              {submitting ? '提交中…' : '📝 提交批改（消耗 1 次额度）'}
            </button>
          )}
          {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

          {myAttempts.length > 0 && (
            <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-3 print:hidden">
              <p className="text-xs font-medium text-gray-500">批改记录（点击查看）</p>
              {myAttempts.map((a) => {
                const v = a.grading?.verdict ? VERDICTS[a.grading.verdict] : null;
                return (
                  <button
                    key={a._id}
                    onClick={() => loadAttempt(a._id)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-gray-50"
                  >
                    {v ? `${v.icon} ${v.label}` : a.status === 'grading' ? '⏳ 批改中' : '❗ 失败'}
                    <span className="ml-auto text-xs text-gray-400">{new Date(a.createdAt || '').toLocaleString('zh-CN')}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* 批改结果 */}
          {activeAttempt && (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              {activeAttempt.status === 'grading' && <p className="animate-pulse text-sm text-brand-600">批改中…</p>}
              {activeAttempt.status === 'error' && (
                <p className="text-sm text-red-600">批改失败：{activeAttempt.errorMsg}（额度已退还，可重新提交）</p>
              )}
              {activeAttempt.status === 'graded' && activeAttempt.grading && (
                <>
                  <div className={`rounded-xl border p-3 ${VERDICTS[activeAttempt.grading.verdict]?.cls || ''}`}>
                    <span className="text-base font-bold">
                      {VERDICTS[activeAttempt.grading.verdict]?.icon} {VERDICTS[activeAttempt.grading.verdict]?.label}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(activeAttempt.grading.steps || []).map((s: Step, i: number) => (
                      <div key={i} className="flex gap-2 rounded-lg bg-gray-50 p-3 text-sm">
                        <span className={`font-bold ${SEVERITIES[s.severity]?.cls || ''}`}>{SEVERITIES[s.severity]?.icon}</span>
                        <Markdown text={s.comment} className="text-sm" />
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-brand-200 bg-brand-50 p-3 text-sm text-gray-800">
                    <span className="font-medium">总结：</span>
                    <Markdown text={activeAttempt.grading.summary} className="text-sm" />
                  </div>
                  {activeAttempt.image && (
                    <details>
                      <summary className="cursor-pointer text-sm text-gray-500">我的手写作答</summary>
                      <img src={activeAttempt.image} alt="作答" className="mt-2 max-h-96 rounded-lg border border-gray-200" />
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
